/**
 * AI Tools for Contacts
 *
 * Tools that allow Marta to interact with contacts (Hub/CRM) via GraphQL.
 */

import type { ToolDefinition } from './types';
import { graphqlQuery, graphqlMutation, CONTACT_FIELDS } from '../../database/graphqlClient';

// =============================================================================
// GRAPHQL QUERIES & MUTATIONS
// =============================================================================

const QUERIES = {
  contacts: `query ListContacts { contacts { ${CONTACT_FIELDS} } }`,
};

const MUTATIONS = {
  createContact: `mutation CreateContact($input: ContactInput!) { createContact(input: $input) { ${CONTACT_FIELDS} } }`,
  deleteContact: `mutation DeleteContact($id: ID!) { deleteContact(id: $id) }`,
};

// =============================================================================
// TOOL DEFINITIONS
// =============================================================================

export const searchContactsTool: ToolDefinition = {
  name: 'search_contacts',
  description: 'Cerca contatti. Può filtrare per nome, azienda, tag o stato lead.',
  category: 'contacts',
  operation: 'read',
  requiresConfirmation: false,
  graphqlOperation: QUERIES.contacts,
  parameters: {
    type: 'object',
    properties: {
      searchTerm: {
        type: 'string',
        description: 'Termine di ricerca per nome, email, azienda',
      },
      company: {
        type: 'string',
        description: 'Filtra per azienda',
      },
      tag: {
        type: 'string',
        description: 'Filtra per tag',
      },
      leadStatus: {
        type: 'string',
        description: 'Filtra per stato lead',
        enum: ['new', 'contacted', 'qualified', 'proposal', 'negotiation', 'won', 'lost'],
      },
      limit: {
        type: 'number',
        description: 'Numero massimo di risultati (default 20)',
      },
    },
  },
};

export const getContactTool: ToolDefinition = {
  name: 'get_contact',
  description: 'Ottiene i dettagli completi di un contatto specifico.',
  category: 'contacts',
  operation: 'read',
  requiresConfirmation: false,
  graphqlOperation: QUERIES.contacts,
  parameters: {
    type: 'object',
    properties: {
      id: {
        type: 'string',
        description: 'ID del contatto',
      },
      name: {
        type: 'string',
        description: 'Nome del contatto (alternativa all\'ID)',
      },
    },
  },
};

export const createContactTool: ToolDefinition = {
  name: 'create_contact',
  description: 'Crea un nuovo contatto nel CRM.',
  category: 'contacts',
  operation: 'create',
  requiresConfirmation: false,
  graphqlOperation: MUTATIONS.createContact,
  parameters: {
    type: 'object',
    properties: {
      displayName: {
        type: 'string',
        description: 'Nome visualizzato del contatto',
      },
      firstName: {
        type: 'string',
        description: 'Nome',
      },
      lastName: {
        type: 'string',
        description: 'Cognome',
      },
      email: {
        type: 'string',
        description: 'Indirizzo email',
      },
      phone: {
        type: 'string',
        description: 'Numero di telefono',
      },
      whatsapp: {
        type: 'string',
        description: 'Numero WhatsApp',
      },
      telegram: {
        type: 'string',
        description: 'Username Telegram',
      },
      linkedin: {
        type: 'string',
        description: 'Profilo LinkedIn',
      },
      company: {
        type: 'string',
        description: 'Azienda',
      },
      role: {
        type: 'string',
        description: 'Ruolo/Posizione',
      },
      notes: {
        type: 'string',
        description: 'Note sul contatto',
      },
      tags: {
        type: 'array',
        items: { type: 'string' },
        description: 'Tag per categorizzare il contatto',
      },
      leadStatus: {
        type: 'string',
        description: 'Stato del lead',
        enum: ['new', 'contacted', 'qualified', 'proposal', 'negotiation', 'won', 'lost'],
      },
      leadSource: {
        type: 'string',
        description: 'Fonte del lead',
        enum: ['website', 'referral', 'linkedin', 'cold_call', 'event', 'other'],
      },
    },
    required: ['displayName'],
  },
};

export const deleteContactTool: ToolDefinition = {
  name: 'delete_contact',
  description: 'Elimina un contatto. ATTENZIONE: Questa azione è irreversibile e rimuoverà anche tutte le conversazioni associate.',
  category: 'contacts',
  operation: 'delete',
  requiresConfirmation: true,
  graphqlOperation: MUTATIONS.deleteContact,
  parameters: {
    type: 'object',
    properties: {
      id: {
        type: 'string',
        description: 'ID del contatto da eliminare',
      },
      contactName: {
        type: 'string',
        description: 'Nome del contatto (per conferma)',
      },
    },
    required: ['id'],
  },
};

export const findContactByNameTool: ToolDefinition = {
  name: 'find_contact_by_name',
  description: 'Trova un contatto cercando per nome.',
  category: 'contacts',
  operation: 'read',
  requiresConfirmation: false,
  graphqlOperation: QUERIES.contacts,
  parameters: {
    type: 'object',
    properties: {
      name: {
        type: 'string',
        description: 'Nome (o parte del nome) del contatto',
      },
    },
    required: ['name'],
  },
};

// =============================================================================
// TOOL EXECUTION FUNCTIONS
// =============================================================================

interface Contact {
  id: string;
  displayName?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  company?: string;
  role?: string;
  tags?: string[];
  leadStatus?: string;
  [key: string]: unknown;
}

export async function executeSearchContacts(params: Record<string, unknown>): Promise<unknown> {
  const result = await graphqlQuery<{ contacts: Contact[] }>(QUERIES.contacts);
  let contacts = result.contacts || [];

  // Apply search term filter
  if (params.searchTerm) {
    const term = String(params.searchTerm).toLowerCase();
    contacts = contacts.filter(
      c =>
        c.displayName?.toLowerCase().includes(term) ||
        c.firstName?.toLowerCase().includes(term) ||
        c.lastName?.toLowerCase().includes(term) ||
        c.email?.toLowerCase().includes(term) ||
        c.company?.toLowerCase().includes(term)
    );
  }

  // Apply company filter
  if (params.company) {
    const company = String(params.company).toLowerCase();
    contacts = contacts.filter(c => c.company?.toLowerCase().includes(company));
  }

  // Apply tag filter
  if (params.tag) {
    const tag = String(params.tag).toLowerCase();
    contacts = contacts.filter(c => c.tags?.some(t => t.toLowerCase().includes(tag)));
  }

  // Apply lead status filter
  if (params.leadStatus) {
    contacts = contacts.filter(c => c.leadStatus === params.leadStatus);
  }

  // Sort by displayName
  contacts.sort((a, b) => (a.displayName || '').localeCompare(b.displayName || ''));

  // Apply limit
  const limit = typeof params.limit === 'number' ? params.limit : 20;
  contacts = contacts.slice(0, limit);

  return { contacts, count: contacts.length };
}

export async function executeGetContact(params: Record<string, unknown>): Promise<unknown> {
  const result = await graphqlQuery<{ contacts: Contact[] }>(QUERIES.contacts);
  const contacts = result.contacts || [];

  let contact: Contact | undefined;

  if (params.id) {
    contact = contacts.find(c => c.id === params.id);
  } else if (params.name) {
    const name = String(params.name).toLowerCase();
    contact = contacts.find(
      c =>
        c.displayName?.toLowerCase().includes(name) ||
        c.firstName?.toLowerCase().includes(name) ||
        c.lastName?.toLowerCase().includes(name)
    );
  }

  if (!contact) {
    return { error: 'Contatto non trovato', found: false };
  }

  return { contact, found: true };
}

export async function executeCreateContact(params: Record<string, unknown>): Promise<unknown> {
  const input = {
    displayName: params.displayName,
    firstName: params.firstName || null,
    lastName: params.lastName || null,
    handles: {
      email: params.email || null,
      phone: params.phone || null,
      whatsapp: params.whatsapp || null,
      telegram: params.telegram || null,
      linkedin: params.linkedin || null,
    },
    company: params.company || null,
    role: params.role || null,
    notes: params.notes || null,
    tags: params.tags || [],
    avatarUrl: null,
    leadStatus: params.leadStatus || null,
    leadSource: params.leadSource || null,
  };

  const result = await graphqlMutation<{ createContact: unknown }>(MUTATIONS.createContact, { input });
  return result.createContact;
}

export async function executeDeleteContact(params: Record<string, unknown>): Promise<unknown> {
  await graphqlMutation(MUTATIONS.deleteContact, { id: params.id });
  return { deleted: true, id: params.id };
}

export async function executeFindContactByName(params: Record<string, unknown>): Promise<unknown> {
  const result = await graphqlQuery<{ contacts: Contact[] }>(QUERIES.contacts);
  const contacts = result.contacts || [];

  const name = String(params.name).toLowerCase();
  const matches = contacts.filter(
    c =>
      c.displayName?.toLowerCase().includes(name) ||
      c.firstName?.toLowerCase().includes(name) ||
      c.lastName?.toLowerCase().includes(name)
  );

  return {
    contacts: matches,
    count: matches.length,
    exactMatch: matches.length === 1,
  };
}

// =============================================================================
// EXPORTS
// =============================================================================

export const contactTools: ToolDefinition[] = [
  searchContactsTool,
  getContactTool,
  createContactTool,
  deleteContactTool,
  findContactByNameTool,
];

export const contactToolExecutors: Record<string, (params: Record<string, unknown>) => Promise<unknown>> = {
  search_contacts: executeSearchContacts,
  get_contact: executeGetContact,
  create_contact: executeCreateContact,
  delete_contact: executeDeleteContact,
  find_contact_by_name: executeFindContactByName,
};
